import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { FormCol } from "components/UI/Form/FormCol.tsx";
import { FormGrid } from "components/UI/Form/FormGrid.tsx";
import { TagSelect } from "components/UI/TagSelect/TagSelect.tsx";
import { TextArea } from "components/UI/TextArea/TextArea.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useUser } from "hooks/useUser.ts";
import { FC, useEffect, useMemo, useState } from "react";

import { buildPixelCode, type ConversionType } from "./utils/buildPixelCode.ts";

interface ConversionPixelFormProps {
    conversionType: ConversionType;
}

export const ConversionPixelForm: FC<ConversionPixelFormProps> = ({ conversionType }) => {
    const { t } = usePaldockTranslation("forms.conversion-pixel");
    const userUtils = useUser();
    const tenantSlug = userUtils.getActiveTenant()?.slug_name ?? "YOUR_TENANT_ID";
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [pixelCode, setPixelCode] = useState("");

    const isInitiation = conversionType === "initiation";

    const initiationTagsOptions = useMemo(
        () => [
            { label: "expiration", value: "expiration" },
            { label: "pcidParam", value: "pcidParam" },
        ],
        []
    );

    const createTagsOptions = useMemo(
        () => [
            { label: "commission_id", value: "commission_id" },
            { label: "external_id", value: "external_id" },
            { label: "value", value: "value" },
            { label: "result", value: "result" },
            { label: "commission", value: "commission" },
            { label: "affcid", value: "affcid" },
            { label: "affs1", value: "affs1" },
            { label: "affs2", value: "affs2" },
            { label: "affs3", value: "affs3" },
            { label: "advs1", value: "advs1" },
            { label: "advs2", value: "advs2" },
            { label: "advs3", value: "advs3" },
            { label: "queryParam", value: "queryParam" },
            { label: "cookieParam", value: "cookieParam" },
        ],
        []
    );

    const updateTagsOptions = useMemo(
        () => [
            { label: "external_id", value: "external_id" },
            { label: "value", value: "value" },
            { label: "result", value: "result" },
            { label: "commission", value: "commission" },
            { label: "affcid", value: "affcid" },
            { label: "affs1", value: "affs1" },
            { label: "affs2", value: "affs2" },
            { label: "affs3", value: "affs3" },
            { label: "advs1", value: "advs1" },
            { label: "advs2", value: "advs2" },
            { label: "advs3", value: "advs3" },
            { label: "queryParam", value: "queryParam" },
            { label: "cookieParam", value: "cookieParam" },
        ],
        []
    );

    const conversionTagsOptions = conversionType === "lead" ? updateTagsOptions : createTagsOptions;

    const tagsOptions = isInitiation ? initiationTagsOptions : conversionTagsOptions;

    // Regenerate the base snippet on type/tenant/tag changes, but preserve manually-edited field
    // values (see buildPixelCode) across regeneration — the previous pixelCode is read fresh from
    // state via the updater form of setState, not captured in the effect's own closure, since it
    // must reflect the latest value even if the user just typed into the textarea.
    useEffect(() => {
        setPixelCode((current) =>
            buildPixelCode({
                conversionType,
                tags: selectedTags,
                tenantSlug,
                pixelCdnUrl: import.meta.env.VITE_PIXEL_CDN_URL,
                pixelVersion: import.meta.env.VITE_PIXEL_VERSION,
                currentPixelCode: current,
            })
        );
    }, [conversionType, selectedTags, tenantSlug]);

    const handleTagsChange = (tags: string[]) => {
        setSelectedTags(tags);
    };

    return (
        <DrawerContent>
            <DrawerCollapsible title={t("sections.settings")}>
                <FormCol>
                    <FormGrid cols={1}>
                        <TextArea
                            value={pixelCode}
                            onChange={(e) => setPixelCode(e.target.value)}
                            appearance="readonly"
                            rows={isInitiation ? 3 : 10}
                        />
                        <TagSelect options={tagsOptions} value={selectedTags} onChange={handleTagsChange} />
                    </FormGrid>
                </FormCol>
            </DrawerCollapsible>
        </DrawerContent>
    );
};
